'use client';

import { ButtonHTMLAttributes, ForwardedRef, MouseEventHandler, ReactNode, useState } from 'react';

import { Styles, css, cx } from '@flipster/styled-system/css';
import { button, type ButtonVariantProps } from '@flipster/styled-system/recipes';

import { IsomorphicLink, IsomorphicLinkProps } from '../design-utils/isomorphic-link';
import { Spinner } from './spinner';

interface BaseProps {
  // variants
  hierarchy?: ButtonVariantProps['hierarchy'];
  color?: ButtonVariantProps['color'];
  size?: ButtonVariantProps['size'];
  // slots
  icon?: ReactNode;
  label?: ReactNode;
  subLabel?: ReactNode;
  css?: Styles;
}

type ButtonElementProps = BaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps | 'className' | 'children'> & {
    ref?: ForwardedRef<HTMLButtonElement>;
    isLoading?: boolean;
    withSpinner?: boolean;
  };
type LinkElementProps = BaseProps &
  Omit<IsomorphicLinkProps, keyof BaseProps | 'className' | 'children'> & {
    ref?: ForwardedRef<HTMLAnchorElement>;
  };
export type ButtonProps<T extends 'button' | 'a' | undefined = 'button'> = { as?: T } & (T extends 'button'
  ? ButtonElementProps
  : LinkElementProps);

const ButtonComponent = ({
  hierarchy,
  color,
  size,
  icon,
  label,
  subLabel,
  css: cssProps,
  withSpinner,
  isLoading,
  disabled,
  onClick,
  ref,
  ...htmlProps
}: ButtonElementProps) => {
  const [waiting, setWaiting] = useState(false);

  const buttonOnClick: MouseEventHandler<HTMLButtonElement> = async (e) => {
    try {
      setWaiting(true);
      // oxlint-disable-next-line @typescript-eslint/await-thenable
      await onClick?.(e);
    } finally {
      setWaiting(false);
    }
  };

  const classes = button({ hierarchy, color, size });
  const showSpinner = withSpinner && (isLoading || waiting);
  const isNeutral = color === 'neutral';

  return (
    <button
      ref={ref}
      type='button'
      role='button'
      className={cx(classes.root, css(cssProps))}
      data-loading={showSpinner ? 'true' : undefined}
      aria-label={showSpinner ? 'Loading' : undefined}
      disabled={isLoading || waiting || disabled}
      onClick={buttonOnClick}
      {...htmlProps}
    >
      {icon && (
        <div aria-label='icon' className={classes.icon}>
          {icon}
        </div>
      )}
      {label && <div className={classes.label}>{label}</div>}
      {subLabel && <div className={classes.subLabel}>{subLabel}</div>}
      {showSpinner && (
        <div className={classes.spinner}>
          <Spinner color={isNeutral ? 'gray' : 'white'} />
        </div>
      )}
    </button>
  );
};

// Link 형식은 isLoading 이 필요없으므로 제외
const LinkComponent = ({
  hierarchy,
  color,
  size,
  icon,
  label,
  ref,
  subLabel,
  css: cssProps,
  ...htmlProps
}: LinkElementProps) => {
  const classes = button({ hierarchy, color, size });

  return (
    <IsomorphicLink ref={ref} className={cx(classes.root, css(cssProps))} {...htmlProps}>
      {icon && <div className={classes.icon}>{icon}</div>}
      {label && <div className={classes.label}>{label}</div>}
      {subLabel && <div className={classes.subLabel}>{subLabel}</div>}
    </IsomorphicLink>
  );
};

export const Button = <T extends 'button' | 'a' | undefined = 'button'>({
  as = 'button',
  ...htmlProps
}: ButtonProps<T>) => {
  if (as === 'button') {
    return <ButtonComponent {...(htmlProps as ButtonElementProps)} />;
  }
  if (as === 'a') {
    return <LinkComponent {...(htmlProps as LinkElementProps)} />;
  }
  return null;
};
Button.displayName = 'Button';
